import { EngineApi } from '@ookla/speedtest-js-sdk'
import config from 'src/Config'
import { useState, useEffect, useContext } from 'react'
import { SpeedTestState } from 'src/Common/SpeedTestState'
import { One_Mbps_To_Byte } from 'src/Common/Constant'
import { SpeedCheckContext } from 'src/lib/ookla_speed_test/SpeedCheckContext'

type StageName = 'latency' | 'download' | 'upload' | 'save'

interface TestError {
  errorKey?: string
  errorMessage?: string
  errorObject?: any
}

export interface SortedServer {
  sponsor: string
  name: string
  country: string
}

export const useSpeedTestEngine = () => {
  const [testState, setTestState] = useState<SpeedTestState>(
    SpeedTestState.IDEAL
  )
  const { state, dispatch } = useContext(SpeedCheckContext)
  const latency = state.ping
  const download = state.download
  const upload = state.upload
  const isSpeedCheckRunning = state.isSpeedCheckRunning
  const [progress, setProgress] = useState<number>(0)
  const [error, setError] = useState<TestError | undefined>()
  const sortedServer = state.sortedServer
  const clientIP = state.clientIP
  const isOptimalLocationLoaded = state.isOptimalLocationLoaded
  const [guid, setGuid] = useState<string>()

  const engine = EngineApi.configure(config)

  const searchServer = () => {
    engine
      .searchServers({
        search: '',
      })
      .then((servers: any) => {
        console.log('Load server detail')
        engine.sortServers(servers).then((sortedServers: any) => {
          dispatch({
            type: 'SORTED_SERVER',
            payload: {
              ...state,
              sortedServer: sortedServers.serverList[0],
              isOptimalLocationLoaded: true,
            },
          })
        })
      })
  }
  const startTest = () => {
    if (!isSpeedCheckRunning) {
      engine.startTest(sortedServer)
      dispatch({
        type: 'SPEED_CHECK_RUNNING_STATE',
        payload: { ...state, isSpeedCheckRunning: true },
      })
    }
  }
  const stopTest = () => {
    engine.stopTest()
  }
  useEffect(() => {
    engine.onStarted.add(() => {
      setTestState(SpeedTestState.PINGING)
    })
    engine.onCanceled.add(() => {
      console.log('onCanceled')
    })
    engine.onProgress.add((stageName: StageName, result: any) => {
      const progressRatio = Math.floor(result.ratioComplete * 100)

      const speed = Math.floor(result.speed / One_Mbps_To_Byte)
      console.log(`${stageName} ${speed} Mbps (${progressRatio}%)`)
      const latency = result.latency
      switch (stageName) {
        case 'latency':
          setTestState(SpeedTestState.PINGING)
          dispatch({
            type: 'SPEED_CHECK_PING',
            payload: { ...state, ping: latency },
          })
          break
        case 'download':
          setTestState(SpeedTestState.DOWNLOADING)
          dispatch({
            type: 'SPEED_CHECK_DOWNLOAD',
            payload: { ...state, download: speed },
          })
          setProgress(progressRatio)
          break
        case 'upload':
          setTestState(SpeedTestState.UPLOADING)
          dispatch({
            type: 'SPEED_CHECK_UPLOAD',
            payload: { ...state, upload: speed },
          })
          setProgress(progressRatio)
          break
      }
    })
    engine.onStageFinished.add((stageName: StageName, result: any) => {
      console.log(
        `onStageFinished(stageName = ${stageName}, result = ${JSON.stringify(
          result
        )})`
      )
      const speed = Math.floor(result.speed / One_Mbps_To_Byte)
      const latency = result.latency
      setProgress(1)
      switch (stageName) {
        case 'latency':
          dispatch({
            type: 'SPEED_CHECK_PING',
            payload: { ...state, ping: latency },
          })
          dispatch({
            type: 'CLIENT_IP_ADDRESS',
            payload: { ...state, clientIP: result.clientAddress },
          })
          break
        case 'download':
          dispatch({
            type: 'SPEED_CHECK_DOWNLOAD',
            payload: { ...state, download: speed },
          })
          break
        case 'upload':
          dispatch({
            type: 'SPEED_CHECK_UPLOAD',
            payload: { ...state, upload: speed },
          })
          break
        case 'save':
          setGuid(Date.now().toString())
          break
      }
    })
    engine.onError.add(
      (errorKey: string, errorMessage: string, errorObject: any) => {
        console.log(
          `onError: errorKey = ${errorKey}, errorMessage = ${errorMessage}, errorObject = ${JSON.stringify(
            errorObject
          )}`
        )
        setError({ errorKey, errorMessage, errorObject })
        setTestState(SpeedTestState.ERROR)
      }
    )
    engine.onFinished.add(() => {
      console.log('onFinished')
      setTestState(SpeedTestState.COMPLETED)
    })
  }, [])

  return {
    testState,
    startTest,
    stopTest,
    latency,
    download,
    upload,
    error,
    progress,
    searchServer,
    sortedServer,
    clientIP,
    guid,
    isOptimalLocationLoaded,
  }
}
